import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch, BASE_URL } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, BarraResumoFiltro } from "../components/ui/ThFiltro";
import { dataHoraLocal } from "../utils/tempo";

const EVENTO_TIPO_LABEL = { 2: "Empresa", 5: "Funcionário" };
const EVENTO_OPERACAO_LABEL = { I: "Cadastro", A: "Alteração", E: "Exclusão" };

function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function PainelAfd() {
  const { token } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [inicio, setInicio] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [fim, setFim] = useState(hojeISO());
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/empresas", undefined, token)
      .then((data) => {
        setEmpresas(data);
        if (data.length === 1) setEmpresaId(String(data[0].id));
      })
      .catch(() => {});
  }, [token]);

  const gerarAfd = async () => {
    if (!empresaId) {
      setErro("Escolha a empresa (CNPJ).");
      return;
    }
    setGerando(true);
    setErro("");
    try {
      const res = await fetch(`${BASE_URL}/api/relatorios/afd?inicio=${inicio}&fim=${fim}&empresa_id=${empresaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.erro || "Não foi possível gerar o AFD.");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afd-${inicio}-a-${fim}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErro(e.message);
    } finally {
      setGerando(false);
    }
  };

  return (
    <Card style={{ marginBottom: 20, padding: 20 }}>
      <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>AFD (Arquivo Fonte de Dados)</h2>
      <p style={{ fontSize: 12.5, color: COLORS.textMid, margin: "0 0 14px" }}>
        Arquivo que um fiscal do trabalho pode pedir numa auditoria (Portaria MTP 671/2021). Baixa um .zip com o arquivo e a assinatura digital. Um AFD é sempre de uma única empresa (CNPJ) por vez.
      </p>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: COLORS.textMid, fontWeight: 600 }}>
          Empresa (CNPJ)
          <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: RADIUS.sm, border: `1px solid ${COLORS.border}`, fontSize: 13, minWidth: 220 }}>
            <option value="">Selecione...</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.nome}{e.cnpj ? ` — ${e.cnpj}` : ""}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: COLORS.textMid, fontWeight: 600 }}>
          Início
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: RADIUS.sm, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: COLORS.textMid, fontWeight: 600 }}>
          Fim
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: RADIUS.sm, border: `1px solid ${COLORS.border}`, fontSize: 13 }} />
        </label>
        <Btn onClick={gerarAfd} disabled={gerando}>{gerando ? "Gerando..." : "Baixar AFD"}</Btn>
        {erro && <span style={{ fontSize: 12.5, color: COLORS.danger }}>{erro}</span>}
      </div>
    </Card>
  );
}

const TIPO_LABEL = {
  entrada: "Entrada",
  saida: "Saída",
  intervalo_inicio: "Início do intervalo",
  intervalo_fim: "Fim do intervalo",
};

export default function Auditoria() {
  const { token, usuario } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);

  const [filtroData, setFiltroData] = useState("");
  const [filtroFuncionario, setFiltroFuncionario] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroExcluidoPor, setFiltroExcluidoPor] = useState("");
  const [filtroMotivo, setFiltroMotivo] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const data = await apiFetch("GET", "/api/ponto/auditoria-exclusoes", undefined, token);
      setRegistros(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const carregarEventos = useCallback(async () => {
    setLoadingEventos(true);
    try {
      const data = await apiFetch("GET", "/api/ponto/eventos-sistema", undefined, token);
      setEventos(data);
    } catch {
      // seção secundária — uma falha aqui não deve esconder o resto da página
    } finally {
      setLoadingEventos(false);
    }
  }, [token]);

  useEffect(() => { carregarEventos(); }, [carregarEventos]);

  useEffect(() => { carregar(); }, [carregar]);

  const tiposDisponiveis = [...new Set(registros.map((r) => r.tipo).filter(Boolean))];
  const registrosFiltrados = registros.filter((r) => {
    if (filtroData) {
      const d = new Date(r.excluido_em);
      const localStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (localStr !== filtroData) return false;
    }
    if (filtroFuncionario && !r.funcionario_nome.toLowerCase().includes(filtroFuncionario.toLowerCase())) return false;
    if (filtroTipo && r.tipo !== filtroTipo) return false;
    if (filtroExcluidoPor && !r.excluido_por_nome.toLowerCase().includes(filtroExcluidoPor.toLowerCase())) return false;
    if (filtroMotivo && !r.motivo.toLowerCase().includes(filtroMotivo.toLowerCase())) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroData || filtroFuncionario || filtroTipo || filtroExcluidoPor || filtroMotivo);
  const limparFiltros = () => {
    setFiltroData(""); setFiltroFuncionario(""); setFiltroTipo(""); setFiltroExcluidoPor(""); setFiltroMotivo("");
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Auditoria
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Toda batida excluída fica registrada aqui — quem excluiu, quando e por quê. Os 100 registros mais recentes.
        </p>
      </div>

      {usuario?.perfil === "admin_empresa" && <PainelAfd />}

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>Cadastros e alterações</h2>
        <p style={{ fontSize: 13, color: COLORS.textMid, margin: "0 0 12px" }}>
          Empresa e funcionários cadastrados, alterados ou excluídos — os 200 eventos mais recentes.
        </p>
        <Card style={{ padding: 0 }}>
          {loadingEventos ? (
            <EstadoCarregando />
          ) : eventos.length === 0 ? (
            <EstadoVazio icon="fileText" texto="Nenhum evento registrado ainda." />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <th style={{ padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textAlign: "left" }}>Quando</th>
                    <th style={{ padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textAlign: "left" }}>Tipo</th>
                    <th style={{ padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textAlign: "left" }}>Ação</th>
                    <th style={{ padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textAlign: "left" }}>Alvo</th>
                    <th style={{ padding: "10px 16px", fontSize: 11.5, fontWeight: 700, color: COLORS.textMid, textAlign: "left" }}>Por</th>
                  </tr>
                </thead>
                <tbody>
                  {eventos.map((e) => (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{dataHoraLocal(e.registrado_em)}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>{EVENTO_TIPO_LABEL[e.tipo_afd] || e.tipo_afd}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>{EVENTO_OPERACAO_LABEL[e.operacao] || "-"}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 700, color: COLORS.text }}>{e.alvo_nome || "-"}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.textMid }}>{e.responsavel_nome || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {!loading && !erro && registros.length > 0 && (
        <BarraResumoFiltro
          total={registrosFiltrados.length}
          totalGeral={registros.length}
          unidade="registro(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : erro ? (
          <div style={{ padding: 24, fontSize: 13.5, color: COLORS.danger }}>{erro}</div>
        ) : registros.length === 0 ? (
          <EstadoVazio icon="fileText" texto="Nenhuma exclusão registrada ainda." />
        ) : registrosFiltrados.length === 0 ? (
          <EstadoVazio icon="fileText" texto="Nenhuma exclusão encontrada com esse filtro." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <ThFiltroTexto label="Excluído em" type="date" value={filtroData} onChange={setFiltroData} minWidth={140} />
                  <ThFiltroTexto label="Funcionário" value={filtroFuncionario} onChange={setFiltroFuncionario} placeholder="Buscar nome..." minWidth={150} />
                  <ThFiltroSelect
                    label="Batida excluída" value={filtroTipo} onChange={setFiltroTipo} minWidth={150}
                    opcoes={[{ value: "", label: "Todas" }, ...tiposDisponiveis.map((t) => ({ value: t, label: TIPO_LABEL[t] || t }))]}
                  />
                  <ThFiltroTexto label="Excluído por" value={filtroExcluidoPor} onChange={setFiltroExcluidoPor} placeholder="Buscar nome..." minWidth={150} />
                  <ThFiltroTexto label="Motivo" value={filtroMotivo} onChange={setFiltroMotivo} placeholder="Buscar..." minWidth={160} />
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.map((r) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>
                      {dataHoraLocal(r.excluido_em)}
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap" }}>
                      {r.funcionario_nome}
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, whiteSpace: "nowrap" }}>
                      {TIPO_LABEL[r.tipo] || r.tipo} às {new Date(r.registrado_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      {r.origem === "manual" && <span style={{ color: COLORS.textLight }}> (lançado manualmente)</span>}
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>
                      {r.excluido_por_nome}
                    </td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>
                      {r.motivo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
